import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Shield, Smartphone, Mail } from 'lucide-react';
import QRCode from 'qrcode';

interface TwoFactorSetupProps {
  onComplete?: () => void;
}

export function TwoFactorSetup({ onComplete }: TwoFactorSetupProps) {
  const [method, setMethod] = useState<'email' | 'authenticator' | null>(null);
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (method === 'authenticator') {
      setupAuthenticator();
    }
  }, [method]);

  const setupAuthenticator = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-totp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const { secret: totpSecret, qrCode: totpQrCode } = await response.json();
      setSecret(totpSecret);
      
      const qrDataUrl = await QRCode.toDataURL(totpQrCode);
      setQrCode(qrDataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to setup authenticator');
    }
  };

  const setupEmailTwoFactor = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/setup-email-2fa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to setup email 2FA');
      }

      setSuccess(true);
      onComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to setup email 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verifyAuthenticator = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-totp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: verificationCode, secret }),
      });

      if (!response.ok) {
        throw new Error('Invalid verification code');
      }

      setSuccess(true);
      onComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-6 bg-green-50 rounded-lg text-center">
        <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-green-900 mb-2">
          Two-Factor Authentication Enabled
        </h3>
        <p className="text-green-700">
          Your account is now more secure with two-factor authentication.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">
        Setup Two-Factor Authentication
      </h2>

      {error && (
        <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}

      {!method ? (
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setMethod('authenticator')}
            className="p-6 border rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <Smartphone className="w-8 h-8 text-indigo-600 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Authenticator App</h3>
            <p className="text-sm text-gray-600">
              Use an authenticator app like Google Authenticator or Authy
            </p>
          </button>

          <button
            onClick={() => setMethod('email')}
            className="p-6 border rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <Mail className="w-8 h-8 text-indigo-600 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Email Authentication</h3>
            <p className="text-sm text-gray-600">
              Receive verification codes via email
            </p>
          </button>
        </div>
      ) : method === 'authenticator' ? (
        <div className="space-y-6">
          <div className="text-center">
            <p className="mb-4">Scan this QR code with your authenticator app:</p>
            {qrCode && (
              <img
                src={qrCode}
                alt="QR Code"
                className="mx-auto mb-6"
                width={200}
                height={200}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter 6-digit code"
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setMethod(null)}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={verifyAuthenticator}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-gray-600">
            We'll send verification codes to your email address when you sign in.
          </p>

          <div className="flex gap-4">
            <button
              onClick={() => setMethod(null)}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={setupEmailTwoFactor}
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Setting up...' : 'Enable Email 2FA'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}